import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
import IamBrokeredSessionsBrowser, {
  type BrokeredSessionFilters,
  // eslint-disable-next-line import/no-unresolved
} from '@devnw/cyber-iam/iam/BrokeredSessionsBrowser/BrokeredSessionsBrowser'
import type { BrokeredSession as IamBrokeredSession } from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../../api/genClient'
import { useActiveTenant } from '../../layout/TenantSwitcher'
import {
  toIamBrokeredSession,
  type WireBrokeredSession,
} from '../../api/brokeredSessionMapping'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

/**
 * OIDCPAM Gate-6 Phase 5c: migrated off MUI onto
 * @devnw/cyber-iam/iam/BrokeredSessionsBrowser — the exact parity component
 * built from this page. The component owns the subject/resource/state filter
 * inputs and the semantic session table; filtering is SERVER-side (the
 * component's Search button hands normalized filters to this page via
 * `onFilter`, which re-queries — there is no live-as-you-type refetch, unlike
 * the pre-migration TextFields). This page still owns the API call, the
 * wire-mapping (../../api/brokeredSessionMapping — fixing a latent
 * camelCase/snake_case bug, see that file's header), tenant gating, and
 * routing to the detail page via `onSelect`.
 */
export const BrokeredSessionsPage = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<IamBrokeredSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(
    (filters?: BrokeredSessionFilters): void => {
      setLoading(true)
      setError(null)
      client
        .adminListBrokeredSessions({
          tenantId: tenant,
          subject: filters?.subjectId,
          resource: filters?.resourceId,
          state: filters?.state,
        })
        .then((resp) => {
          setSessions(
            ((resp.sessions ?? []) as unknown as WireBrokeredSession[]).map(
              toIamBrokeredSession
            )
          )
        })
        .catch((e: unknown) => setError(errMessage(e)))
        .finally(() => setLoading(false))
    },
    [client, tenant]
  )

  // Reload whenever tenant changes (resets to the unfiltered view — the
  // component's local filter inputs are scoped to its own state and reset on
  // remount, so there's nothing stale to reconcile).
  useEffect(() => {
    load()
  }, [load])

  const handleSelect = (session: IamBrokeredSession): void => {
    navigate(`/admin/brokered-sessions/${session.id}`)
  }

  if (tenant === 'system') {
    return (
      <PageContainer>
        <MutedText>
          Please select a tenant to view brokered sessions.
        </MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <IamBrokeredSessionsBrowser
        testID="brokered-sessions"
        title="Brokered Sessions"
        sessions={sessions}
        onFilter={load}
        onSelect={handleSelect}
        loading={loading}
        error={error ?? undefined}
      />
    </PageContainer>
  )
}
